import React from 'react';
import { createBrowserRouter, Params } from 'react-router-dom';

import store from './store';
import { setIsAshford, setLocation } from './store/bookingSlice';

import LocationSelector from './components/LocationSelector';

import {
    rapidstorApi,
    selectLocationBySlug,
} from './features/rapidstor/rapidstorApiSlice';

import './App.scss';

import ContactPage from './routes/ContactPage';
import Error from './routes/Error';
import QuestionnairePage from './routes/QuestionnairePage';
import QuoteSent from './routes/QuoteSent';
import RapidStorPage from './routes/RapidStorPage';
import Root from './routes/Root';
import UnitListing from './routes/UnitListing';

interface LocationParams {
    locationSlug: string;
}

interface OptionalLocationParams {
    locationSlug?: string;
}

const locationLoader = async ({ params }: { params: Params<keyof LocationParams> }) => {
    const promise = store.dispatch(rapidstorApi.endpoints.getLocations.initiate());
    try {
        await promise.unwrap();
        let slug = params.locationSlug as string;
        if (slug === 'ashford') {
            store.dispatch(setIsAshford(true));
            slug = 'keswick';
        }
        const location = selectLocationBySlug(store.getState(), slug);

        if (!location) {
            throw new Response('', {
                status: 404,
                statusText: 'Not Found',
            });
        }

        store.dispatch(setLocation(location));
        return location;
    } finally {
        promise.unsubscribe();
    }
};

const optionalLocationLoader = async ({
    params,
}: {
    params: Params<keyof OptionalLocationParams>;
}) => {
    if (params.locationSlug) {
        return await locationLoader({ params });
    }

    return null;
};

export default createBrowserRouter(
    [
        {
            path: '/',
            element: <Root />,
            errorElement: <Error />,
            children: [
                {
                    path: '',
                    element: <LocationSelector />,
                    errorElement: <Error />,
                },
                {
                    path: 'locations/:locationSlug',
                    element: <QuestionnairePage />,
                    loader: locationLoader,
                    errorElement: <Error />,
                },
                {
                    path: 'locations/:locationSlug/units',
                    element: <UnitListing />,
                    loader: locationLoader,
                    errorElement: <Error />,
                },
                {
                    path: 'locations/:locationSlug/quote-sent',
                    element: <QuoteSent />,
                    loader: locationLoader,
                    errorElement: <Error />,
                },
                {
                    path: 'locations/:locationSlug/booking',
                    element: <RapidStorPage />,
                    loader: locationLoader,
                    errorElement: <Error />,
                },
                {
                    path: 'contact/:locationSlug?',
                    element: <ContactPage />,
                    loader: optionalLocationLoader,
                },
            ],
        },
    ],
    {
        basename: import.meta.env.MODE === 'production' ? '/booking-page' : '/',
    }
);
